import { tz, TZDate } from '@date-fns/tz';
import {
  format,
  formatISO,
  isSameDay,
  isValid,
  isToday,
  parse,
  parseISO,
} from 'date-fns';

/**
 * @example '2022-04-10'
 */
export const toISODateFormat = (date: Date): string =>
  formatISO(date, { representation: 'date' });

/**
 * Converts an ISO timestamp to a short date string (e.g. "Fri, Jan 26").
 * Returns "Today" if the given timestamp is today. A timezone may optionally
 * be provided for determining today.
 */
export const parseToDayMonthFormat = (
  isoDate: string,
  timezone?: string,
): string => {
  const date = parseISO(isoDate, { in: timezone ? tz(timezone) : undefined });
  const now = timezone ? TZDate.tz(timezone) : new Date();

  if (isSameDay(date, now, { in: timezone ? tz(timezone) : undefined })) {
    return 'Today';
  }
  return format(date, 'EEE, MMM d');
};

/**
 * @param date Date object – e.g., parseISO('2024-10-25')
 * @returns 'Fri'
 */
export const toShortDayFormat = (date: Date): string => format(date, 'EEE');

export const todayInTimezone = (timezone: string): string =>
  toISODateFormat(TZDate.tz(timezone));

/**
 * @param date ISO date string – e.g., '2026-02-02'
 * @param timezone e.g., 'America/New_York'
 *
 * @example '1/30'
 * @example 'Today'
 */
export const toShortMonthDayFormat = (
  date: string,
  timezone: string,
): string => {
  if (date === todayInTimezone(timezone)) return 'Today';
  return format(parseISO(date), 'M/d');
};

/**
 * @example 'January 1, 2012'
 */
export const toMonthDateAndYear = (date: Date): string =>
  format(date, 'LLLL d, yyyy');

/**
 * @param date ISO date string – e.g., '2012-01-01T12:00:00'
 *
 * @example 'Jan 1, 2012'
 * @example 'Today'
 */
export const parseToShortDateFormat = (
  date: string,
  formatToday = true,
): string => {
  const parsedDate = parseISO(date);
  return toShortDateFormat(parsedDate, formatToday);
};

/**
 * @param date Date object – e.g., new Date()
 *
 * @example 'Jan 1, 2012'
 * @example 'Today'
 */
export const toShortDateFormat = (date: Date, formatToday = true): string => {
  if (formatToday) return isToday(date) ? 'Today' : format(date, 'MMM d, yyyy');

  return format(date, 'MMM d, yyyy');
};

/**
 * @param startDate e.g., '2012-01-01'
 * @param endDate e.g., '2012-01-02' or null
 *
 * @example 'Jan 1, 2012 – Jan 2, 2012'
 * @example 'Jan 1, 2012 – Endless'
 */
export const parseToShortDateRange = (
  startDate: string,
  endDate: string | null,
): string =>
  `${parseToShortDateFormat(startDate)} – ${endDate ? parseToShortDateFormat(endDate) : 'Endless'}`;

/**
 * @example 'Jan 1, 2012, 2:00 PM'
 */
export const toShortDateTime = (date: Date): string => format(date, 'PPp');

/**
 * @example 'Sun, Jan 1'
 * @example 'Today'
 */
export const toShortWeekDateFormat = (date: Date): string =>
  isToday(date) ? 'Today' : format(date, 'EEE, MMM d');

/**
 * @example 'Sun, Jan 1, 2025'
 * @example 'Today'
 */
export const toShortWeekdayMonthDateAndYearFormat = (date: Date): string =>
  isToday(date) ? 'Today' : format(date, 'EEE, MMM d, yyyy');

/**
 * @example 'Sun, January 1, 2012'
 */
export const toWeekdayMonthDateAndYear = (date: Date): string =>
  format(date, 'EEE, LLLL d, yyyy');

/**
 * @example 'Sunday, January 1, 2012'
 */
export const toLongWeekdayMonthDateAndYear = (date: Date): string =>
  format(date, 'EEEE, LLLL d, yyyy');

/**
 * @example 'Jan 1, 2012 @ 12:00 PM'
 */
export const toPrettyDateTime = (date: Date): string =>
  format(date, 'MMM d, yyyy @ hh:mm a');

/**
 * @param time e.g., '07:00:00'
 * @example '7:00 AM'
 * Returns '' for invalid or empty time strings.
 */
export const toShortTimeOfDay = (time: string): string => {
  const date = parse(time ?? '', 'HH:mm:ss', new Date());
  return isValid(date) ? format(date, 'h:mm a') : '';
};

/**
 * @param date ISO date string – e.g., '2026-02-02'
 * @example 'Dec 14'
 */
export const toShortMonthNameDayFormat = (date: string): string =>
  format(parseISO(date), 'MMM d');

/**
 * @param date ISO date string – e.g., '2026-02-02'
 * @example '2/2/26'
 */
export const toShortNumericDateFormat = (date: string): string =>
  format(parseISO(date), 'M/d/yy');

/**
 * Used to build the event ticket purchase page's URL date segment.
 *
 * @param date ISO date string – e.g., '2026-12-24'
 * @example '24-12-2026'
 */
export const toEventUrlDateFormat = (date: string): string =>
  format(parseISO(date), 'dd-MM-yyyy');

/**
 * Formats an ISO timestamp in the restaurant timezone (wall clock).
 *
 * @param isoTimestamp e.g. '2012-01-01T15:30:00.000Z'
 * @param timezone e.g. 'America/New_York'
 *
 * @example 'January 1, 2012 10:30 AM'
 */
export const getCurrentTimeOfTheRestaurant = (
  isoTimestamp: string,
  timezone?: string,
): string => {
  const inTz = timezone ? tz(timezone) : undefined;
  const date = parseISO(isoTimestamp, { in: inTz });
  if (!isValid(date)) {
    return '';
  }
  return format(date, 'MMMM d, yyyy h:mm a', { in: inTz });
};
